import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import Header from "@components/shared/header";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";

import colors from "@styles/variables/compareCRMPricesVariables.module.scss";
import ContentintellIgenceLogo from "@components/contentintelligence/logo";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";

const Modal = dynamic(() => import("@components/shared/modal"));
const Footer = dynamic(() => import("@components/shared/footer"));
const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

export default function ContentIntelligenceWrapper({
    children,
}: {
    children: ReactNode;
}): ReactElement {
    const { modal, closeModal } = useModal();

    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return (
                    <PrivacyPolicy
                        category="CMS"
                        domain="ContentIntelligence.com"
                    />
                );
            case "terms":
                return (
                    <TermsAndConditions
                        category="CMS"
                        domain="ContentIntelligence.com"
                    />
                );
            case "sideWidget":
                return <Mobile />;
            default:
                return null;
        }
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700&display=swap"
                    rel="stylesheet"
                />
            </Head>

            <div style={{ fontFamily: `'Poppins', sans-serif` }}>
                <Header
                    slogan="TOP ENTERPRISE CONTENT MANAGEMENT COMPANIES"
                    textColor={colors.offerCallNumber}
                >
                    <ContentintellIgenceLogo />
                </Header>
                <main className="max-w-7xl mx-auto block px-4vw lg:px-4">
                    {children}
                </main>
                <Footer />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={
                        modalContent === "privacy"
                            ? "Privacy Policy"
                            : modalContent === "terms"
                            ? "Terms and Conditions"
                            : modalContent === "sideWidget"
                            ? "Contents"
                            : undefined
                    }
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
